import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, input, OnInit, output, signal, TemplateRef, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionService } from '../../services/session.service';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { TableColumn } from '../../../@vex/interfaces/table-column.interface';
import { stagger40ms } from 'src/@vex/animations/stagger.animation';
import { fadeInUp400ms } from 'src/@vex/animations/fade-in-up.animation';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LayoutService } from '../../../@vex/services/layout.service';
import { Icons } from '../../util/Icons';
import { Page, RealEstate, RealEstateContact, RealEstateService, ViewQuery } from '@eadsysteme/ewsrealestateclient-v2';
import { forkJoin, Observable, Subject } from 'rxjs';
import { BreadCrumbService } from '../../../@vex/components/breadcrumbs/BreadCrumbService';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { DateUtil } from 'src/app/util/DateUtil';
import { Router } from '@angular/router';
import { IconHelper } from '../devices/IconHelper';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { DelayedSearchDirective } from '../../directives/delayed-search.directive';

import { BillingViewStrategy, determineViewType, MainViewStrategy, RealEstateViewStrategy, ViewType } from './strategies';
import { Billing } from '@eadsysteme/billingclient-v1';
import { PageLayoutComponent } from '../../../@vex/components/page-layout/page-layout.component';
import { PageLayoutHeaderDirective } from '../../../@vex/components/page-layout/page-layout-header.directive';
import { PageLayoutContentDirective } from '../../../@vex/components/page-layout/page-layout-content.directive';
import { BreadcrumbsComponent } from '../../../@vex/components/breadcrumbs/breadcrumbs.component';
import { AraPaginatorComponent } from '../util/ara-paginator/ara-paginator.component';
import SortTypeEnum = ViewQuery.SortTypeEnum;

export interface AddressTableEntity {
	street?: string;
	zipCode?: string;
	city?: string;
}

export interface CompanyEntity {
	address?: AddressTableEntity;
	companyNames?: string[];
}

export interface RealEstateTableEntity {
	realEstateId?: string;
	realEstateArea?: number;
	realEstateNumber?: number;
	propertyManagementNumber?: string;
	propertyManagementAccountNumber?: string;
	companyName?: string;
	address?: AddressTableEntity;
	propertyManagementCompany?: CompanyEntity;
	lastInspectionDate?: string;
	nextInspectionDate?: string;
	inspectionState?: string;
	operationalState?: string;
}

@Component({
	selector: 'ara-realestate-legacy',
	imports: [
		CommonModule,
		TranslocoModule,
		MatTableModule,
		MatSortModule,
		MatIconModule,
		MatButtonModule,
		MatCheckboxModule,
		MatTooltipModule,
		MatProgressSpinnerModule,
		MatInputModule,
		DelayedSearchDirective,
		PageLayoutComponent,
		PageLayoutHeaderDirective,
		PageLayoutContentDirective,
		BreadcrumbsComponent,
		AraPaginatorComponent,
		MatPaginatorModule,
	],
	templateUrl: './real-estates-legacy.component.html',
	styleUrls: ['./real-estates-legacy.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				appearance: 'outline',
			} as MatFormFieldDefaultOptions,
		},
		MainViewStrategy,
		BillingViewStrategy,
	],
})
export class RealEstatesLegacyComponent implements OnInit {
	protected layoutService = inject(LayoutService);
	protected dateUtil = inject(DateUtil);
	protected icons = inject(Icons);
	protected breadCrumbService = inject(BreadCrumbService);
	protected sessionService = inject(SessionService);
	protected iconHelper = inject(IconHelper);
	private readonly translocoService = inject(TranslocoService);
	private readonly router = inject(Router);
	private readonly realEstateService = inject(RealEstateService);

	private readonly destroyRef = inject(DestroyRef);
	// Strategies
	private readonly mainViewStrategy = inject(MainViewStrategy);
	private readonly billingViewStrategy = inject(BillingViewStrategy);

	dataSource: MatTableDataSource<RealEstateTableEntity>;
	readonly sort = viewChild(MatSort);
	readonly araPaginatorRef = viewChild(AraPaginatorComponent);

	readonly realEstatesTableTemplate = viewChild<TemplateRef<unknown>>('realEstatesTableTemplate');

	/** Emits when a row is clicked - allows custom handling in dialogs */
	readonly rowClicked = output<RealEstateTableEntity>();

	/** When true, renders only the table without page layout wrapper (for use in dialogs) */
	readonly dialogMode = input(false);

	@Input() columns: TableColumn<RealEstate>[] = [];
	finishedLoading = signal(false);
	searchFilterSubject = new Subject<string>();
	private currentSearchValue = '';
	private currentSortField = '';
	private currentSortDirection: ViewQuery.SortTypeEnum = 'ASC';

	readonly currentPage = signal(0);
	currentPageSize = 20;
	totalElements = signal(0);
	readonly pageSizeOptions = [10, 20, 50, 100];

	get isPaginatedMode(): boolean {
		return this.sessionService.isDevRole() || this.sessionService.isSupportRole();
	}

	isMainRoute = signal(false);
	isSmokeDetectorRoute = signal(false);
	isBillingRoute = signal(false);

	selection = new SelectionModel<RealEstateTableEntity>(true, []);
	selectedRealEstateIds: Set<string> = new Set();
	deSelectedRealEstateIds: Set<string> = new Set();
	selectToggleEnabled = signal(false);
	allSelected = signal(false);
	private readonly realestateById = new Map<string, RealEstateTableEntity>();

	viewStrategy: RealEstateViewStrategy;

	constructor() {
		this.isMainRoute.set(true);
		this.isSmokeDetectorRoute.set(false);
		this.initializeViewStrategy();
	}

	get visibleColumns() {
		return this.columns.filter((column) => column.visible).map((column) => column.property);
	}

	ngOnInit(): void {
		sessionStorage.setItem('selectedRealEstateID', '');
		sessionStorage.setItem('selectedTenantUnitID', '');
		sessionStorage.setItem('activeView', globalThis.location.href);

		this.breadCrumbService.reset();
		this.breadCrumbService.addBreadCrumb({
			translationKey: 'realestates.realestates',
			icon: this.icons.get('realEstate'),
		});

		this.searchFilterSubject.next('');

		this.searchFilterSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filterValue: string) => {
			this.finishedLoading.set(false);
			this.applyFilter(filterValue);
		});

		if (this.viewStrategy.onInit) {
			this.viewStrategy.onInit();
		}

		this.getData();
	}

	private initializeViewStrategy(): void {
		const viewType = determineViewType(globalThis.location.href);

		if (viewType === ViewType.BILLING) {
			this.viewStrategy = this.billingViewStrategy;
			this.isBillingRoute.set(true);
		} else {
			this.viewStrategy = this.mainViewStrategy;
			this.isMainRoute.set(true);
		}

		this.columns = this.viewStrategy.getColumns(this.getBaseColumns(), this.translate.bind(this));
	}

	private getBaseColumns(): TableColumn<RealEstate>[] {
		return [
			{ label: this.translate('realestates.area'), property: 'area', type: 'text', visible: true },
			{
				label: this.translate('address.address'),
				property: 'address',
				type: 'text',
				visible: true,
			},
			{
				label: this.translate('realestates.propertyManagement'),
				property: 'propertyManagement',
				type: 'text',
				visible: true,
			},
		];
	}

	toTableEntity(realEstateContact: RealEstateContact): RealEstateTableEntity {
		// Transform a RealEstate Object to a RealEstateTableEntity Object
		const address: AddressTableEntity = {
			street: realEstateContact.realEstateAddressStreet,
			zipCode: realEstateContact.realEstateAddressZipCode,
			city: realEstateContact.realEstateAddressCity,
		};
		// NOSONAR: deprecated fields (contactName/contactOverwritingName*) — no API replacement available yet
		const name1 = realEstateContact.contactName ?? realEstateContact.contactOverwritingName1;
		const name2 = realEstateContact.contactName ? realEstateContact.contactOverwritingName2 : realEstateContact.contactOverwritingName1;
		const name3 = realEstateContact.contactName ? realEstateContact.contactOverwritingName3 : realEstateContact.contactOverwritingName2;
		return {
			realEstateId: realEstateContact.realEstateId,
			realEstateArea: realEstateContact.area,
			realEstateNumber: realEstateContact.realEstateNumber,
			propertyManagementNumber: realEstateContact.propertyManagementNumber,
			propertyManagementAccountNumber: realEstateContact.contactAccountNumber,
			companyName: name1,
			propertyManagementCompany: {
				companyNames: [name1, name2, name3],
			},
			address,
		};
	}

	applyFilter(filterValue: string): void {
		this.currentSearchValue = filterValue;
		if (this.isPaginatedMode) {
			this.currentPage.set(0);
			this.resetPaginator();
		}
		const search: ViewQuery = {};
		search.searchValue = filterValue;
		search.sortType = this.currentSortDirection;
		search.sortField = this.currentSortField;
		this.searchData(search);
	}

	translate(key: string): string {
		return this.translocoService.translate(key);
	}

	getServiceCompanyName(realEstateArea: number): string {
		return this.sessionService.cachedServiceCompanyNames.get(realEstateArea)?.replace(',', '');
	}

	getAreaTooltip(row: RealEstateTableEntity): string {
		if (!row?.realEstateArea) {
			return '';
		}
		const serviceCompany = this.getServiceCompanyName(row.realEstateArea);
		const areaLabel = this.translocoService.translate('realestates.area');
		const numberLabel = this.translocoService.translate('realestates.number');
		return `${serviceCompany}\n${areaLabel}: ${row.realEstateArea}\n${numberLabel}: ${row.realEstateNumber}`;
	}

	onRealEstate(row: RealEstateTableEntity): void {
		if (this.dialogMode()) {
			this.rowClicked.emit(row);
			return;
		}
		sessionStorage.setItem('selectedRealEstateID', row.realEstateId);
		sessionStorage.setItem('selectedRealEstateArea', row.realEstateArea.toString());
		this.viewStrategy.onRowClick(row, this.router);
	}

	sortData(sort: Sort): void {
		if (!sort.active || sort.direction === '') {
			return;
		}

		const search: ViewQuery = {};
		search.searchValue = this.currentSearchValue;
		search.sortType = sort.direction === 'asc' ? SortTypeEnum.ASC : SortTypeEnum.DESC;
		search.sortField = sort.active;
		this.currentSortField = search.sortField;
		this.currentSortDirection = search.sortType;
		if (this.isPaginatedMode) {
			this.currentPage.set(0);
			this.resetPaginator();
		}
		this.searchData(search);
	}

	onPageChange(event: PageEvent): void {
		this.currentPage.set(event.pageIndex);
		this.currentPageSize = event.pageSize;
		const search: ViewQuery = {
			searchValue: this.currentSearchValue,
			sortType: this.currentSortDirection,
			sortField: this.currentSortField,
		};
		this.searchData(search);
	}

	private resetPaginator(): void {
		const pag = this.araPaginatorRef()?.paginator();
		if (pag) {
			pag.pageIndex = 0;
		}
	}

	private getData(): void {
		this.applyFilter(this.currentSearchValue);
	}

	private createFilterPredicate(): (data: RealEstateTableEntity, filter: string) => boolean {
		return (realEstate: RealEstateTableEntity, filter: string): boolean => {
			const addressText = `${realEstate.address?.street ?? ''}${realEstate.address?.city ?? ''}${realEstate.address?.zipCode ?? ''}`;
			const companyText = `${realEstate.propertyManagementCompany?.companyNames?.[0] ?? ''}${realEstate.propertyManagementCompany?.companyNames?.[1] ?? ''}`;
			const numberText = `${realEstate.realEstateArea ?? ''}${realEstate.realEstateNumber ?? ''}`;
			const searchableText = `${numberText}${addressText}${companyText}`.toLowerCase();
			return searchableText.includes(filter.trim().toLowerCase());
		};
	}

	private searchData(search: ViewQuery): void {
		this.finishedLoading.set(false);

		switch (search.sortField) {
			case 'area':
				search.sortField = 'number';
				break;
			case 'propertyManagementName':
				search.sortField = 'name';
				break;
			case 'propertyManagementAccountNumber':
				search.sortField = 'accountNumber';
				break;
			case 'address':
				search.sortField = 'addressStreet';
				break;
			default:
				break;
		}

		if (this.isPaginatedMode) {
			// When you open this view as Dev/Support User, you receive all RealEstates of all Areas you are part of
			search.size = this.currentPageSize;
			search.page = this.currentPage();

			this.realEstateService
				.search(this.sessionService.effectiveArea, search)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((page: Page) => {
					this.totalElements.set(page.totalElements);
					this.setTableData(page.items);
				});
		} else {
			search.size = 1000;
			search.page = 0;

			this.realEstateService
				.search(this.sessionService.effectiveArea, search)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((firstPage: Page) => {
					const allItems: RealEstateContact[] = [...firstPage.items];

					if (firstPage.totalElements > allItems.length) {
						const remainingPages = Math.ceil((firstPage.totalElements - allItems.length) / 1000);
						const pageRequests: Observable<Page>[] = [];
						for (let i = 1; i <= remainingPages; i++) {
							pageRequests.push(this.realEstateService.search(this.sessionService.effectiveArea, { ...search, page: i }));
						}
						forkJoin(pageRequests)
							.pipe(takeUntilDestroyed(this.destroyRef))
							.subscribe((pages: Page[]) => {
								for (const page of pages) {
									allItems.push(...page.items);
								}
								this.setTableData(allItems);
							});
					} else {
						this.setTableData(allItems);
					}
				});
		}
	}

	private setTableData(items: RealEstateContact[]): void {
		const realEstateTableEntities: RealEstateTableEntity[] = items.map((item) => this.toTableEntity(item));
		this.dataSource = new MatTableDataSource(realEstateTableEntities);
		this.viewStrategy.enrichRowData(this.dataSource, this.destroyRef);
		this.dataSource.filterPredicate = this.createFilterPredicate();
		this.finishedLoading.set(true);
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	masterToggle(): void {
		this.selectToggleEnabled.set(!this.selectToggleEnabled());
		this.selection.clear();

		if (this.selectToggleEnabled()) {
			for (const row of this.dataSource.data) {
				this.selection.select(row);
				this.realestateById.set(row.realEstateId, row);
			}
			this.allSelected.set(true);
		} else {
			this.realestateById.clear();
			this.allSelected.set(false);
			this.deSelectedRealEstateIds = new Set();
			this.selectedRealEstateIds = new Set();
		}
	}

	getIconUrl(area: number): string {
		const base64 = this.sessionService.cachedFavIconsByArea.get(area);
		return base64 ? `data:image/png;base64,${base64}` : '';
	}

	onBillingClick(row: RealEstateTableEntity, billing: Billing): void {
		this.sessionService.effectiveArea = row.realEstateArea;
		if (this.viewStrategy instanceof BillingViewStrategy) {
			this.viewStrategy.onBillingChipClick(billing);
		}
	}
}
